import { gql } from 'graphql-request';

import { MultiSelectOptionType } from '@parspec/pixel';

import { reportingAxiosInstance as axios } from 'src/app/Axios';
import { FilterType, FILTER_KEYS } from '../shared/components/Filters/constants';
import {
    AnalyticsRequestProps,
    BranchLocationsTabDataResponse,
    CustomersTabDataResponse,
    ManufacturersTabDataResponse,
    ParamProps,
    QuoteOwnersTabDataResponse,
    ReportingPageNameTypes
} from './apiTypes';
import { graphqlRequest } from 'src/app/graphqlClient';

const getStringFromArray = (val: MultiSelectOptionType[]) => {
    return val.map((item) => item.value).join(',');
};

const getQueryParams = (filters: FilterType) => {
    const params = {} as ParamProps;
    if (filters[FILTER_KEYS.PROJECT_STAGES]?.length) {
        params.project_stage = getStringFromArray(filters[FILTER_KEYS.PROJECT_STAGES]);
    }
    if (filters[FILTER_KEYS.BRANCH_LOCATIONS]?.length) {
        params.branch_location = getStringFromArray(filters[FILTER_KEYS.BRANCH_LOCATIONS]);
    }
    if (filters[FILTER_KEYS.OWNERS]?.length) {
        params.quote_owner = getStringFromArray(filters[FILTER_KEYS.OWNERS]);
    }
    if (filters[FILTER_KEYS.MARKET_VERTICALS]?.length) {
        params.market_vertical = getStringFromArray(filters[FILTER_KEYS.MARKET_VERTICALS]);
    }
    if (filters[FILTER_KEYS.SCOPE]?.length) {
        params.bom_scope = getStringFromArray(filters[FILTER_KEYS.SCOPE]);
    }
    if (filters[FILTER_KEYS.DATE_RANGE]?.length) {
        params.quote_due_date_start = filters[FILTER_KEYS.DATE_RANGE][0];
        params.quote_due_date_end = filters[FILTER_KEYS.DATE_RANGE][1];
    }
    return params;
};

export const endpoints = {
    customersTabUrl: `/reporting/customers`,
    branchLocationsTabUrl: `/reporting/locations`,
    quoteOwnersTabUrl: `/reporting/users`,
    manufacturersTabUrl: `/reporting/manufacturers`
};

export const getCustomersTabData = async (filters: FilterType) => {
    const params = getQueryParams(filters);
    return axios.get<CustomersTabDataResponse[]>(endpoints.customersTabUrl, {
        ...(Object.keys(params).length ? { params } : {})
    });
};

export const getBranchLocationsTabData = async (filters: FilterType) => {
    const params = getQueryParams(filters);
    return axios.get<BranchLocationsTabDataResponse[]>(endpoints.branchLocationsTabUrl, {
        ...(Object.keys(params).length ? { params } : {})
    });
};

export const getQuoteOwnersTabData = async (filters: FilterType) => {
    const params = getQueryParams(filters);
    return axios.get<QuoteOwnersTabDataResponse[]>(endpoints.quoteOwnersTabUrl, {
        ...(Object.keys(params).length ? { params } : {})
    });
};

export const getManufacturersTabData = async (filters: FilterType) => {
    const params = getQueryParams(filters);
    return axios.get<ManufacturersTabDataResponse[]>(endpoints.manufacturersTabUrl, {
        ...(Object.keys(params).length ? { params } : {})
    });
};

// #################################################################################
// ########################## FOR MANAGE COLUMNS ###################################
// #################################################################################

export const getSelectedColumns = async (payload: { userId: number; pageName: ReportingPageNameTypes }) => {
    const queryString = gql`
        query GetSelectedColumns($userId: Int!, $pageName: page_name_type!) {
            events_db {
                table_settings(where: { page_name: { _eq: $pageName }, user_id: { _eq: $userId } }) {
                    order
                    filters
                }
            }
        }
    `;
    const response = await graphqlRequest({
        document: queryString,
        variables: { userId: payload.userId, pageName: payload.pageName }
    });
    return response.events_db;
};

export const updateSelectedColumns = async (payload: { pageName: ReportingPageNameTypes; columnData: string[] }) => {
    const upsertMutationString = gql`
        mutation UpsertSelectedColumns($order: [String!]!, $pageName: page_name_type!) {
            events_db {
                insert_table_settings(objects: { order: $order, page_name: $pageName }, on_conflict: { constraint: table_settings_pkey, update_columns: order }) {
                    affected_rows
                }
            }
        }
    `;
    await graphqlRequest({
        document: upsertMutationString,
        variables: { order: payload.columnData, pageName: payload.pageName }
    });
};

export const insertAnalyicsData = async (payload: AnalyticsRequestProps) => {
    const insertMutationString = `mutation ($tab_name: String!, $action_performed: String!, $filter_used: String) {
        logging_db{
            insert_reporting_dashboard_analytics(objects: {tab_name: $tab_name, action_performed: $action_performed, filter_used: $filter_used}) {
          affected_rows
        }}
      }`;
    return graphqlRequest({ document: insertMutationString, variables: { ...payload } });
};
